import { Certificate } from '@/entities/certificate';
import { getUserCertificates } from '@/use-cases/get-user-signatures';
import { VisibleSignatureOptions } from '@/use-cases/sign-uploaded-document';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import notifyHB from '@/config/notifyHB';
import { isHttpError } from '@/http';
import { Loader2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SignaturePlacement } from './zip-placement-picker';
import styles from './sign-zip-modal.module.scss';
import { useAuthContext } from '@/components/auth-context/auth-context';
import { getUserName } from '@/utils/get-user-name';
import { toast } from '@/components/toast/toast';
import { signZIP } from '@/use-cases/sign-zip';
import { PinSignInput } from '@/components/pin-sign-input/pin-sign-input';

// Standard A4 page dimensions in PDF points
const A4_WIDTH_PTS = 595.28;
const A4_HEIGHT_PTS = 841.89;
const SIG_WIDTH_PTS = A4_WIDTH_PTS * 0.28;
const SIG_HEIGHT_PTS = A4_HEIGHT_PTS * 0.07;

type SignZipModalProps = {
	zipFile: File;
	placement?: SignaturePlacement;
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
};

export function SignZipModal(props: SignZipModalProps) {
	const { userProfile } = useAuthContext();
	const [certificates, setCertificates] = useState<Certificate[]>([]);
	const [selectedCertificate, setSelectedCertificate] = useState<
		Certificate['certificate_id'] | null
	>(null);
	const [isFetchingCertificates, setIsFetchingCertificates] = useState(false);
	const [otp, setOtp] = useState('');
	const [isSigning, setIsSigning] = useState(false);

	useEffect(() => {
		if (props.isOpen) {
			setIsFetchingCertificates(true);
			getUserCertificates()
				.then((serverCertificates) => {
					if (serverCertificates) {
						setCertificates(
							serverCertificates.filter(
								(certificate) =>
									certificate.status === 'Active',
							),
						);
					}
				})
				.catch((error) => {
					toast({
						description:
							'Ocurrió un error inesperado. Intenta de nuevo más tarde.',
						mode: 'destructive',
					});
					notifyHB('sign-zip', 'SignZipModal', error);
				})
				.finally(() => setIsFetchingCertificates(false));
		} else {
			setSelectedCertificate(null);
			setOtp('');
		}
	}, [props.isOpen]);

	useEffect(() => {
		if (!selectedCertificate) {
			setOtp('');
			return;
		}
	}, [selectedCertificate]);

	const handleSign = async () => {
		if (!selectedCertificate || !otp) return;

		try {
			const visibleSignatureOptions = props.placement
				? {
						page_number: props.placement.pageNumber - 1,
						position_x: props.placement.xPct * A4_WIDTH_PTS,
						position_y: props.placement.yPct * A4_HEIGHT_PTS,
						width: SIG_WIDTH_PTS,
						height: SIG_HEIGHT_PTS,
						motive: `firmado electrónicamente por ${getUserName(userProfile) ?? ''}`,
					}
				: undefined;

			setIsSigning(true);
			const result = await signZIP(
				props.zipFile,
				selectedCertificate,
				{ type: 'pin', value: otp },
				visibleSignatureOptions,
			);
			if (result.success) {
				toast({
					description: 'ZIP firmado con éxito',
					mode: 'informative',
				});
				props.onClose();
			} else {
				toast({
					title: 'Tus documentos no fueron firmados',
					description: 'Comunícate con soporte',
					mode: 'informative',
				});
				console.warn(result);
				notifyHB('sign-zip', 'SignZipModal', result);
			}
		} catch (error) {
			if (isHttpError(error) && error.status === 400) {
				toast({
					description: 'El PIN ingresado no es válido',
					mode: 'caution',
				});
			} else {
				notifyHB('sign-zip', 'SignZipModal', error);
				toast({
					description:
						'No es posible firmar. Intenta de nuevo más tarde',
					mode: 'destructive',
				});
			}
		} finally {
			setIsSigning(false);
		}
	};

	return (
		<Dialog
			open={props.isOpen}
			onOpenChange={(open) => (open ? props.onOpen() : props.onClose())}
		>
			<DialogContent
				aria-describedby={undefined}
				className={styles['sign-zip-modal']}
			>
				<DialogTitle className={styles['title']}>
					Selecciona un certificado
				</DialogTitle>
				<div className={styles['input-group']}>
					¿Qué certificado quieres usar?
					<Select
						value={selectedCertificate ?? ''}
						onValueChange={setSelectedCertificate}
						disabled={isFetchingCertificates}
					>
						<SelectTrigger className="w-[180px]">
							<SelectValue placeholder="Selecciona un certificado" />
							{isFetchingCertificates && (
								<Loader2Icon className="animate-spin" />
							)}
						</SelectTrigger>
						<SelectContent>
							{certificates.map((cert) => (
								<SelectItem
									key={cert.certificate_id}
									value={cert.certificate_id}
								>
									{cert.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{selectedCertificate && (
					<div className={styles['otp-section']}>
						<PinSignInput
							value={otp}
							setValue={setOtp}
							disabled={isSigning}
							autoFocus
						/>
					</div>
				)}

				<DialogFooter>
					<DialogClose asChild>
						<Button variant="secondary" disabled={isSigning}>
							Cancelar
						</Button>
					</DialogClose>
					<Button
						onClick={handleSign}
						disabled={!selectedCertificate || !otp || isSigning}
					>
						Firmar
						{isSigning && <Loader2Icon className="animate-spin" />}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
